// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Channel} from '@app/features/channel/models/Channel';
import {ContextMenuCloseProvider, MenuGroup} from '@app/features/ui/action_menu/ContextMenu';
import {MenuItemRadio} from '@app/features/ui/action_menu/MenuItemRadio';
import {Button} from '@app/features/ui/button/Button';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import type {VoiceParticipantAvatarEntry} from '@app/features/voice/components/VoiceParticipantAvatarList';
import {buildVoiceParticipantIdentity} from '@app/features/voice/utils/VoiceParticipantIdentity';
import VoiceSceneTvRequest from '@app/features/voice_scene/state/VoiceSceneTvRequest';
import {roomCanvasOf} from '@app/features/voice_scene/utils/RoomCanvas';
import type {TvSource} from '@app/features/voice_scene/video/TvSource';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {MonitorIcon, PresentationIcon, VideoCameraIcon, YoutubeLogoIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const TV_SOURCE_DESCRIPTOR = msg({
	message: 'TV source',
	comment: '3D voice room TV: button under the TV that opens the list of what this viewer can put on it.',
});
const SCREEN_SOURCE_DESCRIPTOR = msg({
	message: "{displayName}'s screen",
	comment: "3D voice room TV source list: someone's screen share. {displayName} is that person's name.",
});
const CAMERA_SOURCE_DESCRIPTOR = msg({
	message: "{displayName}'s camera",
	comment: "3D voice room TV source list: someone's camera. {displayName} is that person's name.",
});
const LINK_SOURCE_DESCRIPTOR = msg({
	message: "{displayName}'s video",
	comment:
		"3D voice room TV source list: the YouTube video someone plays on the TV for the room. {displayName} is that person's name.",
});

const STAGE_SOURCE_DESCRIPTOR = msg({
	message: "{displayName}'s stage",
	comment:
		"3D voice room TV source list: the board or video a bot shows on the TV as the room's host. {displayName} is the bot's name.",
});

const SOURCE_DESCRIPTORS = {
	stage: STAGE_SOURCE_DESCRIPTOR,
	screen: SCREEN_SOURCE_DESCRIPTOR,
	camera: CAMERA_SOURCE_DESCRIPTOR,
	link: LINK_SOURCE_DESCRIPTOR,
} as const;

function SourceIcon({kind}: {kind: TvSource['kind']}) {
	if (kind === 'stage')
		return <PresentationIcon weight="bold" data-flx="voice-scene.voice-scene-tv-picker.presentation-icon" />;
	if (kind === 'screen') return <MonitorIcon weight="bold" data-flx="voice-scene.voice-scene-tv-picker.monitor-icon" />;
	if (kind === 'camera')
		return <VideoCameraIcon weight="bold" data-flx="voice-scene.voice-scene-tv-picker.video-camera-icon" />;
	return <YoutubeLogoIcon weight="bold" data-flx="voice-scene.voice-scene-tv-picker.youtube-logo-icon" />;
}

// what plays on the TV is this viewer's own choice; nobody else's view or the streamer's viewer list changes.
export const VoiceSceneTvPicker = observer(function VoiceSceneTvPicker({
	channel,
	entries,
	sources,
	current,
}: {
	channel: Channel;
	entries: ReadonlyArray<VoiceParticipantAvatarEntry>;
	sources: ReadonlyArray<TvSource>;
	current: TvSource | null;
}) {
	const {i18n} = useLingui();
	const guildId = channel.guildId ?? null;
	const label = (source: TvSource): string => {
		if (source.kind === 'stage') return i18n._(STAGE_SOURCE_DESCRIPTOR, {displayName: source.name});
		const entry = entries.find(
			(item) => buildVoiceParticipantIdentity(item.userId, item.connectionId) === source.identity,
		);
		const displayName = entry ? NicknameUtils.getNickname(entry.user, guildId, channel.id) : '';
		return i18n._(SOURCE_DESCRIPTORS[source.kind], {displayName});
	};
	const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		ContextMenuCommands.openFromElementBottomRight(
			event,
			({onClose}) => (
				<ContextMenuCloseProvider
					value={onClose}
					data-flx="voice-scene.voice-scene-tv-picker.context-menu-close-provider"
				>
					<MenuGroup data-flx="voice-scene.voice-scene-tv-picker.menu-group">
						{sources.map((source) => (
							<MenuItemRadio
								key={`${source.kind}:${source.identity}`}
								selected={source.kind === current?.kind && source.identity === current.identity}
								closeOnSelect
								onSelect={() => VoiceSceneTvRequest.select(channel.id, source)}
								icon={<SourceIcon kind={source.kind} data-flx="voice-scene.voice-scene-tv-picker.source-icon" />}
								data-flx="voice-scene.voice-scene-tv-picker.menu-item-radio.select"
							>
								{label(source)}
							</MenuItemRadio>
						))}
					</MenuGroup>
				</ContextMenuCloseProvider>
			),
			// Closed, the menu gives the focus back to the room, so the walking keys work on.
			{returnFocusTarget: roomCanvasOf(event.currentTarget)},
		);
	};
	return (
		<Button small compact variant="inverted" onClick={handleOpen} data-flx="voice-scene.voice-scene-tv-picker.button">
			{i18n._(TV_SOURCE_DESCRIPTOR)}
		</Button>
	);
});
